import { appName, POSTFIX } from "../constants"
import { all, call, put, takeLatest } from "redux-saga/effects"
import { createSelector } from "reselect"
import { push } from "react-router-redux"
import Http from "../api/design-templates-categories"

import { toastr } from "react-redux-toastr"
import { errorFormatter } from "../utils/errorFormatter"

/**
 * Constants
 **/
export const moduleName = "DTCategories"
const prefix = `${appName}/${moduleName}`

export const LOADING = `${prefix}/LOADING`
export const GET_ROOM_TYPES = `${prefix}/GET_ROOM_TYPES`
export const DELETE_ROOM_TYPE = `${prefix}/DELETE_ROOM_TYPE`
export const GET_FURNITURE_CATEGORIES = `${prefix}/GET_FURNITURE_CATEGORIES`
export const CHECKED_FURNITURE_CATEGORIES = `${prefix}/CHECKED_FURNITURE_CATEGORIES`
export const REMOVE_CHECKED_FURNITURE_CATEGORIES = `${prefix}/REMOVE_CHECKED_FURNITURE_CATEGORIES`
export const CREATE_ROOM_TYPE = `${prefix}/CREATE_ROOM_TYPE`
export const GET_ROOM_TYPE = `${prefix}/GET_ROOM_TYPE`
export const UPDATE_ROOM_TYPE = `${prefix}/UPDATE_ROOM_TYPE`
export const ADD_ROOM_TYPE_CATEGORY = `${prefix}/ADD_ROOM_TYPE_CATEGORY`
export const SAVE_CATEGORIES_VALUES = `${prefix}/SAVE_CATEGORIES_VALUES`

/**
 * Reducer
 **/
const initialState = Object.freeze({
  roomTypes: [],
  room: {},
  furnitureCategories: [],
  checkedFurnitureCategories: [],
  loading: false,
  categoriesValues: {},
  error: null
})

export default (state = initialState, action) => {
  const { type, payload, error } = action

  switch (type) {
    // Loading
    case LOADING + POSTFIX.START:
      return { ...state, loading: true }

    case LOADING + POSTFIX.STOP:
      return { ...state, loading: false }

    // GET roomTypes
    case GET_ROOM_TYPES + POSTFIX.SUCCESSFUL:
      return { ...state, roomTypes: payload.roomTypes }

    case GET_ROOM_TYPES + POSTFIX.FAILURE:
      return { ...state, roomTypes: [], error }

    // DELETE roomTypes
    case DELETE_ROOM_TYPE + POSTFIX.SUCCESSFUL:
      return { ...state, roomTypes: payload.roomTypes }

    case DELETE_ROOM_TYPE + POSTFIX.FAILURE:
      return { ...state, error }

    // GET roomType
    case GET_ROOM_TYPE + POSTFIX.SUCCESSFUL:
      return { ...state, room: payload.room }

    case GET_ROOM_TYPE + POSTFIX.FAILURE:
      return { ...state, error }

    case ADD_ROOM_TYPE_CATEGORY:
      return {
        ...state,
        room: {
          ...state.room,
          style_design_categories_with_parent: payload.categoriesWithParent
        }
      }

    // Save categoriesValues
    case SAVE_CATEGORIES_VALUES:
      return {
        ...state,
        categoriesValues: payload.categoriesValues
      }

    // GET furnitureCategories
    case GET_FURNITURE_CATEGORIES + POSTFIX.SUCCESSFUL:
      return { ...state, furnitureCategories: payload.furnitureCategories }

    case GET_FURNITURE_CATEGORIES + POSTFIX.FAILURE:
      return { ...state, furnitureCategories: [], error }

    // Checked furnitureCategories
    case CHECKED_FURNITURE_CATEGORIES:
      return { ...state, checkedFurnitureCategories: payload.checkedFurnitureCategories }

    case REMOVE_CHECKED_FURNITURE_CATEGORIES:
      return { ...state, checkedFurnitureCategories: [] }

    default:
      return state
  }
}

/**
 * Selectors
 * reducer name: availableCategoriesReducer
 **/
export const getLoadingSelector = (state) => state.availableCategoriesReducer.loading

export const getRoomTypesSelector = (state) => state.availableCategoriesReducer.roomTypes

export const getFurnitureCategoriesSelector = (state) => state.availableCategoriesReducer.furnitureCategories

export const getCheckedFurnitureCategoriesSelector = (state) => state.availableCategoriesReducer.checkedFurnitureCategories

export const getRoomTypeSelector = (state) => state.availableCategoriesReducer.room

export const getCategoriesValues = (state) => state.availableCategoriesReducer.categoriesValues

// Checked categories
export const addFurnitureCategories = (state) => {
  const categories = getFurnitureCategoriesSelector(state)
  const checkedCategories = getCheckedFurnitureCategoriesSelector(state)

  const data = categories.reduce((acc, category) => {
    acc[category.value] = {
      value: category.value,
      label: category.label,
      children: []
    }

    category.children && category.children.forEach((item) => {
      if (checkedCategories.includes(`${item.value}`)) {
        acc[item.parent_id].children.push(item)
      }
    })

    return acc
  }, {})

  return Object.keys(data).map((item) => data[item])
}

export const makeAddFurnitureCategoriesSelector = () => createSelector(
  [addFurnitureCategories],
  (categories) => categories
)

/**
 * Action Creators
 **/
// Loading
export const startLoadingAction = () => ({ type: LOADING + POSTFIX.START })
export const stopLoadingAction = () => ({ type: LOADING + POSTFIX.STOP })

// GET roomTypes
export const fetchRoomTypesAction = () => ({ type: GET_ROOM_TYPES })
export const fetchRoomTypesSuccessAction = (roomTypes) => ({
  type: GET_ROOM_TYPES + POSTFIX.SUCCESSFUL,
  payload: { roomTypes }
})
export const fetchRoomTypesFailureAction = (error) => ({
  type: GET_ROOM_TYPES + POSTFIX.FAILURE,
  payload: { error }
})

// DELETE roomTypes
export const deleteRoomTypeAction = (roomTypeId) => ({ type: DELETE_ROOM_TYPE, payload: { roomTypeId } })
export const deleteRoomTypeSuccessAction = (roomTypes) => ({
  type: DELETE_ROOM_TYPE + POSTFIX.SUCCESSFUL,
  payload: { roomTypes }
})
export const deleteRoomTypeFailureAction = (error) => ({
  type: DELETE_ROOM_TYPE + POSTFIX.FAILURE,
  payload: { error }
})

// GET roomType
export const getRoomTypeAction = (roomId) => ({ type: GET_ROOM_TYPE, payload: { roomId } })
export const getRoomTypeSuccessfulAction = (room) => ({
  type: GET_ROOM_TYPE + POSTFIX.SUCCESSFUL,
  payload: { room }
})
export const getRoomTypeFailureAction = (error) => ({
  type: GET_ROOM_TYPE + POSTFIX.FAILURE,
  payload: { error }
})
export const addRoomTypeCategory = (categoriesWithParent) => ({
  type: ADD_ROOM_TYPE_CATEGORY,
  payload: { categoriesWithParent }
})

// PUT roomType
export const updateRoomTypeAction = (roomId, roomData) => ({
  type: UPDATE_ROOM_TYPE,
  payload: { roomId, roomData }
})

// GET furnitureCategories
export const fetchFurnitureCategoriesAction = () => ({ type: GET_FURNITURE_CATEGORIES })
export const fetchFurnitureCategoriesSuccessAction = (furnitureCategories) => ({
  type: GET_FURNITURE_CATEGORIES + POSTFIX.SUCCESSFUL,
  payload: { furnitureCategories }
})
export const fetchFurnitureCategoriesFailureAction = (error) => ({
  type: GET_FURNITURE_CATEGORIES + POSTFIX.FAILURE,
  payload: { error }
})

// Checked furnitureCategories
export const checkedFurnitureCategoriesAction = (checkedFurnitureCategories) => ({
  type: CHECKED_FURNITURE_CATEGORIES,
  payload: { checkedFurnitureCategories }
})

export const removeCheckedFurnitureCategoriesAction = () => ({ type: REMOVE_CHECKED_FURNITURE_CATEGORIES })

// POST roomType
export const createRoomTypeAction = (roomData) => ({
  type: CREATE_ROOM_TYPE,
  payload: { roomData }
})

export const saveCategoriesValue = (categoriesValues) => ({
  type: SAVE_CATEGORIES_VALUES,
  payload: { categoriesValues }
})

/**
 * Sagas
 **/
function* fetchRoomTypesSaga() {
  try {
    yield put(startLoadingAction())
    const getRoomTypes = Http.getRoomTypes
    const response = yield call(getRoomTypes)
    yield put(fetchRoomTypesSuccessAction(response))
    yield put(stopLoadingAction())
  }
  catch (error) {
    const errorMessage = errorFormatter(error)
    const showError = toastr.error

    yield put(stopLoadingAction())
    yield put(fetchRoomTypesFailureAction(errorMessage))
    yield call(showError, errorMessage)
  }
}

function* deleteRoomType(action) {
  const { payload: { roomTypeId } } = action

  try {
    const successMessage = "Successfully deleted"
    const showSuccess = toastr.success

    yield put(startLoadingAction())
    const deleteRoomType = Http.deleteRoomType
    const response = yield call(deleteRoomType, roomTypeId)
    yield put(deleteRoomTypeSuccessAction(response))
    yield put(stopLoadingAction())
    yield call(showSuccess, successMessage)
  }
  catch (error) {
    const errorMessage = errorFormatter(error)
    const showError = toastr.error

    yield put(stopLoadingAction())
    yield put(deleteRoomTypeFailureAction(errorMessage))
    yield call(showError, errorMessage)
  }
}

function* fetchFurnitureCategoriesSaga() {
  try {
    yield put(startLoadingAction())
    const getFurnitureCategories = Http.getFurnitureCategories
    const response = yield call(getFurnitureCategories)
    yield put(fetchFurnitureCategoriesSuccessAction(response.categories))
    yield put(stopLoadingAction())
  }
  catch (error) {
    const errorMessage = errorFormatter(error)
    const showError = toastr.error

    yield put(stopLoadingAction())
    yield put(fetchFurnitureCategoriesFailureAction(errorMessage))
    yield call(showError, errorMessage)
  }
}

function* createRoomTypeSaga(action) {
  const { payload: { roomData } } = action

  try {
    const successMessage = "Successfully created"
    const showSuccess = toastr.success

    yield put(startLoadingAction())
    const createRoomType = Http.createRoomType
    yield call(createRoomType, roomData)
    yield put(stopLoadingAction())
    yield call(showSuccess, successMessage)
    yield put(push("/design-templates/detail/categories"))
    yield put(removeCheckedFurnitureCategoriesAction())
  }
  catch (error) {
    const errorMessage = errorFormatter(error)
    const showError = toastr.error

    yield put(stopLoadingAction())
    yield call(showError, errorMessage)
  }
}

function* fetchRoomTypeSaga(action) {
  const { payload } = action

  try {
    yield put(startLoadingAction())
    const getFurnitureCategory = Http.getFurnitureCategory
    const response = yield call(getFurnitureCategory, payload.roomId)
    yield put(getRoomTypeSuccessfulAction(response.room))
    yield put(fetchFurnitureCategoriesSuccessAction(response.categories))
    yield put(stopLoadingAction())
  }
  catch (error) {
    const errorMessage = errorFormatter(error)
    const showError = toastr.error

    yield put(stopLoadingAction())
    yield put(getRoomTypeFailureAction(errorMessage))
    yield call(showError, errorMessage)
  }
}

function* updateRoomTypeSaga(action) {
  const { payload: { roomId, roomData } } = action

  try {
    const successMessage = "Successfully updated"
    const showSuccess = toastr.success

    yield put(startLoadingAction())
    const updateRoomType = Http.updateRoomType
    yield call(updateRoomType, roomId, roomData)
    yield put(stopLoadingAction())
    yield call(showSuccess, successMessage)
    yield put(push("/design-templates/detail/categories"))
    yield put(removeCheckedFurnitureCategoriesAction())
  }
  catch (error) {
    const errorMessage = errorFormatter(error)
    const showError = toastr.error

    yield put(stopLoadingAction())
    yield call(showError, errorMessage)
  }
}

export function* saga() {
  yield all([
    takeLatest(GET_ROOM_TYPES, fetchRoomTypesSaga),
    takeLatest(DELETE_ROOM_TYPE, deleteRoomType),
    takeLatest(GET_FURNITURE_CATEGORIES, fetchFurnitureCategoriesSaga),
    takeLatest(CREATE_ROOM_TYPE, createRoomTypeSaga),
    takeLatest(GET_ROOM_TYPE, fetchRoomTypeSaga),
    takeLatest(GET_ROOM_TYPE, fetchRoomTypeSaga),
    takeLatest(UPDATE_ROOM_TYPE, updateRoomTypeSaga)
  ])
}
